import {Form, Formik} from 'formik'
import {useState} from 'react'
import * as Yup from 'yup'

import {ConnectedFocusError} from 'focus-formik-error'
import moment from 'moment'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import BreadCrumb from 'src/feature/bite/component/Breadcrumb'
import InlineTable from 'src/feature/bite/component/InlineTable'

import {useCreateStore} from 'src/feature/store/context/useMutation'

const LocationDetail = () => {
  const intl = useIntl()
  const [isLoading] = useState(false)
  const {mutateAsync} = useCreateStore()

  const column = [
    [
      {
        title: 'Location Name',
        key: 'name',
        dataIndex: 'name',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Provide a name for the location to help differentiate it from others.',
      },
      {
        title: 'Apt/Building No',
        key: 'aptBuildingNo',
        dataIndex: 'aptBuildingNo',
        edit: 'input',
        maxLength: 51,
        tooltip: 'Specify the apartment or building number for accurate location details.',
        required: true,
      },
      {
        title: 'ZIP Code',
        key: 'zipCode',
        dataIndex: 'zipCode',
        edit: 'input',
        maxLength: 21,
        tooltip: 'Enter the postal code associated with this location.',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.REGION'}),
        key: 'region',
        dataIndex: 'regionName',
        edit: 'region',
        formName: 'regionId',
        tooltip: 'Specify the region, state, or province where the location is situated.',
      },
      {
        title: 'Latitude',
        key: 'latitude',
        dataIndex: 'latitude',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Enter the geographic latitude of the location for mapping accuracy.',
      },
    ],
    [
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        edit: 'select',
        required: true,
        tooltip:
          'Select whether this location is Active or Inactive to manage its visibility on the platform.',
        valueSelect: true,
        selects: [
          // {value: 0, text: 'Passive'},
          {value: 1, text: 'Active'},
          {value: 2, text: 'Inactive'},
        ],
      },
      {
        title: 'Address',
        key: 'addressLine',
        dataIndex: 'addressLine',
        edit: 'input',
        tooltip: 'Enter the full address of the location.',
        required: true,
      },

      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.CITY'}),
        key: 'city',
        dataIndex: 'cityName',
        edit: 'city',
        formName: 'cityID',
        required: true,
        tooltip: 'Enter the city where the location is based.',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.COUNTRY'}),
        key: 'country',
        dataIndex: 'countryName',
        edit: 'country',
        formName: 'countryID',
        displayType: 'text',
        required: true,
        tooltip: 'Select the country where the location operates.',
      },
      {
        title: 'Longitude',
        key: 'longitude',
        dataIndex: 'longitude',
        edit: 'input',
        tooltip: 'Enter the geographic longitude of the location for mapping accuracy.',
        maxLength: 51,
        required: true,
      },
    ],
  ]

  const columnOperationalDetails = [
    [
      {
        title: 'Working Days',
        key: 'workingDays',
        required: true,
        dataIndex: 'workingDays',
        tooltip: 'Select the days when the location is open for business.',
        editable: true,
        edit: 'multiple_select',
        selects: [
          {value: '0', text: 'Sunday'},
          {value: '1', text: 'Monday'},
          {value: '2', text: 'Tuesday'},
          {value: '3', text: 'Wednesday'},
          {value: '4', text: 'Thursday'},
          {value: '5', text: 'Friday'},
          {value: '6', text: 'Saturday'},
        ],
        render: (value: any) => {
          return value?.map((item: any) => {
            return (
              <span key={item} className='badge badge-light me-1'>
                {intl.formatMessage({id: `GLOBAL.DAY.${item}`})}
              </span>
            )
          })
        },
      },
      {
        title: 'Season Start Date',
        key: 'seasonStartDate',
        tooltip: 'Set the date when seasonal operations begin for this location.',
        dataIndex: 'seasonStartDate',
        edit: 'date',
        render: (value: any) => {
          return (
            <>
              {moment().diff(moment.utc(value), 'years') > 100
                ? '-'
                : moment.utc(value).format('DD-MM-YYYY')}
            </>
          )
        },
      },
      {
        title: 'Opening Time',
        key: 'workingOpenHour',
        dataIndex: 'workingOpenHour',
        editable: true,
        edit: 'time',
        required: true,
        tooltip: 'Set the time this location begins daily operations.',
      },
    ],
    [
      {
        title: 'Season Period',
        key: 'seasonPeriod',
        dataIndex: 'seasonPeriod',
        yesOrNo: true,
        editable: true,
        tooltip: 'Indicate whether this location operates seasonally.',
        edit: 'select',
        selects: [
          {value: true, text: 'Yes'},
          {value: false, text: 'No'},
        ],
      },
      {
        title: 'Season End Date',
        key: 'seasonEndDate',
        dataIndex: 'seasonEndDate',
        tooltip: 'Set the date when seasonal operations end for this location.',
        edit: 'date',
        render: (value: any) => {
          return (
            <>
              {moment().diff(moment.utc(value), 'years') > 100
                ? '-'
                : moment.utc(value).format('DD-MM-YYYY')}
            </>
          )
        },
      },
      {
        title: 'Closing Time',
        key: 'workingCloseHour',
        dataIndex: 'workingCloseHour',
        tooltip: 'Set the time this location ends daily operations.',
        editable: true,
        edit: 'time',
        required: true,
      },
    ],
  ]

  const columnContactInformation = [
    [
      {
        key: 'contactFirstName',
        dataIndex: 'contactFirstName',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.NAME'}),
        edit: 'input',
        tooltip: 'Enter the first name of the contact person for this location.',
        required: true,
        maxLength: 51,
      },
      {
        key: 'contactLastName',
        dataIndex: 'contactLastName',
        tooltip: 'Enter the last name of the contact person.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.SURNAME'}),
        edit: 'input',
        required: true,
        maxLength: 51,
      },
    ],
    [
      {
        key: 'contactMobilePhone',
        dataIndex: 'contactMobilePhone',
        formName: 'contactMobilePhone',
        tooltip: 'Provide a phone number for inquiries and business communication.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.PHONE'}),
        edit: 'phone',
        required: true,
      },
      {
        key: 'email',
        dataIndex: 'email',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.EMAIL'}),
        edit: 'input',
        maxLength: 101,
        tooltip: 'Enter the official email address for this location.',
        required: true,
      },
      {
        key: 'contactTitle',
        dataIndex: 'contactTitle',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.TITLE'}),
        edit: 'input',
        tooltip: 'Specify the title or role of the contact person.',
        required: true,
        maxLength: 51,
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    aptBuildingNo: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    zipCode: Yup.string().max(
      10,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 10})
    ),
    regionId: Yup.string(),
    longitude: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .typeError(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -180}))
      .max(180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 180})),

    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    addressLine: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),

    countryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    cityID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    latitude: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .typeError(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -90}))
      .max(90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 90})),
    contactFirstName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    contactLastName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    contactMobilePhone: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    email: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    contactTitle: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    workingOpenHour: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    workingCloseHour: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    workingDays: Yup.array()
      .required('Working days is required')
      .test('workingDays', 'At least one working day is required', (value) => value.length > 0),
    seasonStartDate: Yup.string().when(['seasonPeriod'], ([seasonPeriod], schema) => {
      return seasonPeriod
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .test('seasonStartDate', 'Season start date must be before end date', function (value) {
              const {seasonEndDate} = this.parent
              return moment(seasonEndDate).isAfter(value)
            })
        : schema.notRequired()
    }),
    seasonEndDate: Yup.string().when(['seasonPeriod'], ([seasonPeriod], schema) => {
      return seasonPeriod
        ? schema
            .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
            .test('seasonEndDate', 'Season end date must be after start date', function (value) {
              const {seasonStartDate} = this.parent
              return moment(seasonStartDate).isBefore(value)
            })
        : schema.notRequired()
    }),
    seasonPeriod: Yup.boolean().required('Season period is required'),
  })

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: '',
          aptBuildingNo: '',
          zipCode: '',
          regionId: '',
          longitude: '',
          status: 1,
          addressLine: '',
          countryID: 'f109e594-b301-ec11-a85d-000d3a229408',
          cityID: '',
          latitude: '',
          agreementDate: moment().utc().format('YYYY-MM-DDTHH:mm:ss'),
          contactCountryPhoneCode: '+971',
          contactFirstName: '',
          contactLastName: '',
          contactPhone: '',
          email: '',
          contactTitle: '',
          workingOpenHour: '',
          workingCloseHour: '',
          seasonPeriod: false,
          seasonStartDate: '',
          seasonEndDate: '',
        }}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
        onSubmit={async (values, {setSubmitting, resetForm}) => {
          try {
            const data = {
              ...values,
              contactPhone: values.contactPhone.replace(/\D/g, '').replace('971', ''),
              contactCountryPhoneCode: '+971',
              culture: 'AE',
            }

            const updateResponse = await mutateAsync({values: data})
            if (updateResponse) resetForm()
          } catch (error) {
            toast.error(intl.formatMessage({id: 'ERROR'}))
          }
        }}
      >
        {({values, setFieldValue, errors, touched, resetForm}: any) => (
          <Form>
            <ConnectedFocusError />
            <BreadCrumb parentPage='Location' currentPage='Add New Location' />
            <div className='card my-5 my-xl-10'>
              <div className='card-header'>
                <div className='card-title m-0'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>Location Info</h3>
                    <p className='text-muted fw-normal fs-6'>
                      Add a new business location to ensure accurate service availability and
                      operations.
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body p-9'>
                <InlineTable dataSource={values} columns={column} editable={true} values={values} />
              </div>
            </div>

            <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
              <div className='card-header'>
                <div className='card-title m-0 d-flex justify-content-between w-100'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>Operational Details</h3>
                    <p className='text-muted fw-normal fs-6'>
                      Define the business hours and seasonal availability for this location.
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body p-9'>
                <InlineTable
                  loading={isLoading}
                  dataSource={values}
                  columns={columnOperationalDetails}
                  editable={true}
                  values={values}
                />
              </div>
            </div>

            <div className='card mb-5 mb-xl-10'>
              <div className='card-header'>
                <div className='card-title m-0'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>
                      {intl.formatMessage({id: 'STORE.DETAIL.CONTACT'})}
                    </h3>
                    <p className='text-muted fw-normal fs-6'>
                      Provide the contact information for this location to facilitate communication.
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body p-9'>
                <InlineTable
                  loading={isLoading}
                  dataSource={values}
                  columns={columnContactInformation}
                  editable={true}
                  values={values}
                />
              </div>
            </div>

            <div className='d-flex justify-content-end mt-10'>
              <button type='submit' className='btn btn-dark ms-2 px-10'>
                Create
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default LocationDetail
